// Design & Engineering module — thin helpers over the shared axios client
// (backend: backend/design_engineering/, mounted at /api/design).
import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

export const D = "/design";

// The module's 4xx bodies are either a string or {message, errors[] | duplicates[]}.
export function designError(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.message) {
    const extra = Array.isArray(detail.errors) && detail.errors.length ? `: ${detail.errors.join("; ")}` : "";
    return `${detail.message}${extra}`;
  }
  return formatApiError(detail) || fallback;
}

export function designErrorList(err) {
  const detail = err?.response?.data?.detail;
  return detail && Array.isArray(detail.errors) ? detail.errors : [];
}

// Cached per auth token, so logging out and in as someone else (without a
// page reload) never shows the previous user's design permissions.
let meCache = null;
let mePromise = null;
let meToken = null;

function currentToken() {
  try { return localStorage.getItem("token") || ""; } catch { return ""; }
}

export function useDesignMe() {
  const token = currentToken();
  if (meToken !== token) { meCache = null; mePromise = null; meToken = token; }
  const [me, setMe] = useState(meCache);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (meCache) { setMe(meCache); return; }
    mePromise = mePromise || api.get(`${D}/me`).then((r) => r.data);
    mePromise
      .then((d) => { meCache = d; setMe(d); })
      .catch((e) => { mePromise = null; setError(designError(e)); });
  }, [token]);
  const can = useCallback((p) => !!me?.permissions?.includes(p), [me]);
  return { me, can, error };
}

export function resetDesignMe() {
  meCache = null;
  mePromise = null;
}

// Fetch a protected file as a blob (so the bearer token is sent) and open or save it.
export async function openDesignFile(file, { save = false, stamped = true } = {}) {
  const res = await api.get(`${D}/files/${file.id}/download`, { params: { stamped }, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  if (!save && ["pdf", "png", "jpg", "jpeg", "webp"].includes(file.ext)) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = file.filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadBlob(path, filename, params = {}) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function fmtDate(v) {
  if (!v) return "—";
  const s = String(v);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function fmtSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const label = (s) => String(s || "").replace(/_/g, " ");
